import { useState } from 'react'
import { useContext } from 'react'
import { ModalContext, ModalKey } from 'components/Modals/ModalContext'
import { useDispatch } from 'react-redux'
import { swapActiveWorkoutExercise, undoExerciseSwap } from 'redux/slices/activeWorkout'
import { AreAllSetsForAnExerciseIncomplete, SwapExercise, UndoExerciseSwap } from 'functions/activeWorkoutUtils'
import { useGetExercises } from 'redux/customHooks/useGetExercises'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid'

import ExerciseImage from 'components/ExerciseImage'
import VolumeAssigned from 'components/ActiveWorkout/VolumeAssigned'
import LoadAssigned from 'components/ActiveWorkout/LoadAssigned'
import StatusIndicator from 'components/ActiveWorkout/StatusIndicator'
import VolumeCompleted from 'components/ActiveWorkout/VolumeCompleted'
import LoadCompleted from 'components/ActiveWorkout/LoadCompleted'
// import DifficultyScale from 'components/ActiveWorkout/DifficultyScale'
import ActionButtons from 'components/ActiveWorkout/ActionButtons'
import GroupIndividualExerciseSetListItem from 'components/ActiveWorkout/GroupIndividualExerciseSetListItem'

export default function ExerciseSetListItem({ 
    version = '4.0',
    profileType,
    groupId,
    groupAthletes,
    athleteId,
    workoutType,
    workoutTemplateKey,
    taperData,
    phaseIndex, 
    phase,
    exerciseIndex, 
    exercise, 
    setIndex, 
    set,
    MutateRemainingSets = () => {}
}) {
    const dispatch = useDispatch()

    const { exercises } = useGetExercises()

    const { ShowModal, HideModal } = useContext(ModalContext)

    const [expandGroupAthletes, setExpandGroupAthletes] = useState(false)

    const ShowExerciseInstructionsModal = (exerciseId) => {
        const modalData = {
            key: ModalKey.ExerciseInstructions,
            fullscreen: true,
            exerciseId: exerciseId,
        }

        ShowModal(modalData)
    }

    const OnSwapButton = () => {
        if (AreAllSetsForAnExerciseIncomplete(profileType, exercise) === false) {
            ShowSwapWarningModal()
            return
        }

        const modalData = {
            key: ModalKey.ExerciseSwap,
            fullscreen: true,
            phaseKey: phase.phaseKey,
            currentExercise: exercise,
            selectAction: async (swapType, swapExercise) => {
                SwapExercise(
                    dispatch, 
                    profileType,
                    groupId,
                    athleteId,
                    phaseIndex, 
                    exerciseIndex, 
                    swapType, 
                    swapExercise,
                    swapActiveWorkoutExercise,
                    MutateRemainingSets
                )

                HideModal()
            }
        }

        ShowModal(modalData)
    }

    const OnUndoSwapButton = () => {
        if (AreAllSetsForAnExerciseIncomplete(profileType, exercise) === false) {
            ShowSwapWarningModal()
            return
        }

        UndoExerciseSwap(
            dispatch,
            profileType,
            groupId,
            athleteId,
            phaseIndex,
            exerciseIndex,
            undoExerciseSwap,
            MutateRemainingSets
        )
    }

    const ShowSwapWarningModal = () => {
        const modalData = {
            key: ModalKey.Text,
            fullscreen: false,
            titleText: 'Cannot swap exercise with already saved sets',
            bodyText: [
                'The exercise you are trying to swap already has saved sets.',
                'Please edit any saved sets back to incomplete status before trying to swap.',
                'Contact support@kinesisintegrated.com for more info'
            ],
        }

        ShowModal(modalData)
    }

    /*
    const ApplyMDSRepsAdjustment = () => {
        if (
            (workoutTemplateKey === 'strength-session-a' || workoutTemplateKey === 'strength-session-b') && // Must be a strength session
            phaseIndex !== 0 // Must not be the first phase
        ) {

            var repsAdjustmentPayload = {
                phaseIndex: phaseIndex,
                exerciseIndex: exerciseIndex,
                setIndex: setIndex,
                mutationKey: 'mds-reps-adjustment',
                bumpAmount: 0
            }

            switch (set.completedSetData.movementDifficulty) {
                case -2: repsAdjustmentPayload.bumpAmount = 15; break;
                case -1: repsAdjustmentPayload.bumpAmount = 3; break;
                case 1: repsAdjustmentPayload.bumpAmount = -3; break;
                case 2: repsAdjustmentPayload.bumpAmount = -20; break;
                default: break;
            }

            if (repsAdjustmentPayload.bumpAmount === 0) return;

            // console.log(repsAdjustmentPayload)

            MutateRemainingSets(repsAdjustmentPayload)
        }
    }
    */

    /*
    const ApplyLDSLoadAdjustment = () => {

        const ldsLoadAdjustmentPayload = {
            phaseIndex: phaseIndex,
            exerciseIndex: exerciseIndex,
            setIndex: setIndex,
            mutationKey: 'lds-load-adjustment'
        }

        // console.log(ldsLoadAdjustmentPayload)

        MutateRemainingSets(ldsLoadAdjustmentPayload)
    }
    */

    var isCompleted = false
    var isSkipped = false

    if (profileType === 'group') {
        isCompleted = set.completedSetData.completed
        isSkipped = set.completedSetData.skipped
    }

    if (profileType === 'individual') {
        isCompleted = set.completedSetData.completed
        isSkipped = set.completedSetData.skipped
    }

    const isRecommendation = exercise.exerciseType === 'recommendation'

    if (exercises.length === 0) { 
        return (
            <div className='w-full h-32 p-4'>
                <div className='w-full h-full bg-gray-200 rounded-lg animate-pulse' />
            </div>
        )
    }

    if (phase.expanded === false) {
        return null
    }

    return (
        <div>
            <div className='pt-2 px-4 pb-2 flex items-center gap-x-2 h-12'>
                <StatusIndicator 
                    isCompleted={isCompleted} 
                    isSkipped={isSkipped} 
                />

                <p className='text-xl flex-grow'>
                    {exercise.name}
                </p>
            </div>

            <div className='w-full px-4 pb-4 flex items-center gap-x-4'>
                <div className='space-y-2'>
                    <div className='text-xs text-center text-gray-600'>Instructions</div>

                    <ExerciseImage
                        exercise={exercise}
                        exercises={exercises}
                        className='w-20 h-20'
                        onClick={() => ShowExerciseInstructionsModal(exercise.id)}
                    />
                </div>

                <LoadAssigned exercise={exercise} set={set} isSkipped={isSkipped} isCompleted={isCompleted} />
                
                <VolumeAssigned 
                    version={version}
                    exercise={exercise} 
                    set={set} 
                    isSkipped={isSkipped} 
                    isCompleted={isCompleted} 
                />

                <div className='flex-1'/>

                <LoadCompleted
                    profileType={profileType}
                    groupId={groupId}
                    athleteId={athleteId}
                    phaseIndex={phaseIndex}
                    exerciseIndex={exerciseIndex}
                    exercise={exercise}
                    setIndex={setIndex}
                    set={set}
                    isRecommendation={isRecommendation}
                    isCompleted={isCompleted}
                    isSkipped={isSkipped}
                />
                
                <VolumeCompleted
                    version={version}
                    profileType={profileType}
                    groupId={groupId}
                    athleteId={athleteId}
                    phaseIndex={phaseIndex}
                    exerciseIndex={exerciseIndex}
                    exercise={exercise}
                    setIndex={setIndex}
                    set={set}
                    isCompleted={isCompleted}
                    isSkipped={isSkipped}
                />

                {/* 
                <DifficultyScale
                    profileType={profileType}
                    groupId={groupId}
                    athleteId={athleteId}
                    phaseIndex={phaseIndex}
                    exerciseIndex={exerciseIndex}
                    exercise={exercise}
                    setIndex={setIndex}
                    set={set}
                    isCompleted={isCompleted}
                    isSkipped={isSkipped}
                /> 
                */}

                <ActionButtons
                    version={version}
                    profileType={profileType}
                    groupId={groupId}
                    athleteId={athleteId}
                    // taperData={taperData}
                    // workoutType={workoutType}
                    phaseIndex={phaseIndex}
                    exerciseIndex={exerciseIndex}
                    exercise={exercise}
                    setIndex={setIndex}
                    set={set}
                    isCompleted={isCompleted}
                    isSkipped={isSkipped}
                    isRecommendation={isRecommendation}
                    OnSwapButton={OnSwapButton}
                    OnUndoSwapButton={OnUndoSwapButton}
                    // ApplyMDSRepsAdjustment={ApplyMDSRepsAdjustment}
                    // ApplyLDSLoadAdjustment={ApplyLDSLoadAdjustment}
                    MutateRemainingSets={MutateRemainingSets}
                />

                {profileType === 'group' && groupAthletes?.length > 1 && (
                    <div 
                        className='pr-3 cursor-pointer pt-6'
                        onClick={() => setExpandGroupAthletes(!expandGroupAthletes)}
                    >
                        {!expandGroupAthletes && <ChevronDownIcon className='w-8 h-8 text-gray-600' />}
                        {expandGroupAthletes && <ChevronUpIcon className='w-8 h-8 text-gray-600' />}
                    </div>
                )}
            </div>

            <div className='divide-y divide-gray-200'>
                {profileType === 'group' && expandGroupAthletes && groupAthletes?.length > 1 && groupAthletes.map((athlete, index) => (
                    <div 
                        key={index} 
                        className={`
                            w-full bg-gray-50 flex items-center pr-4 gap-x-4
                            ${set.groupIndividualAthleteDetails[athlete.id].groupIndividualOverrideInputs ? 'bg-orange-100' : ''}
                        `}
                    >
                        <GroupIndividualExerciseSetListItem
                            profileType='group-individual'
                            groupId={groupId}
                            groupAthletes={groupAthletes}
                            athleteId={athlete.id}
                            athleteFirstName={athlete.firstName}
                            athleteLastName={athlete.lastName}
                            groupIndividualOverrideInputs={set.groupIndividualAthleteDetails[athlete.id].groupIndividualOverrideInputs}
                            workoutType={workoutType}
                            taperData={taperData}
                            phaseIndex={phaseIndex}
                            exerciseIndex={exerciseIndex}
                            exercise={exercise}
                            setIndex={setIndex}
                            set={set}
                        />
                    </div>
                ))}
            </div>
        </div>
    )
}